import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Task,
  TaskCompletion,
  TaskCompletionStatusEnum,
  useTaskCompletionStatusUpdateMutation,
} from 'graphql-common';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import navigateToInterventionCreate from 'routes/Intervention/utils/navigateToInterventionCreate';
import getTaskCompletionSelectOption from 'routes/Intervention/utils/getTaskCompletionSelectOption';

// Admin-only completion write actions: status update and create-intervention.
export default function useTaskCompletionWriteActions({
  task,
  taskId,
  onStatusUpdated,
}: {
  // The anchor task: completion rows don't select their task's location.
  task?: Task | null;
  taskId?: string;
  onStatusUpdated?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [updateStatus, { loading: isStatusUpdating }] =
    useTaskCompletionStatusUpdateMutation({
      onCompleted: () => {
        toast({ content: t('task-result-state-successfully-updated') });
        onStatusUpdated?.();
      },
      onError: (error) => showGraphQLErrors(error, t),
    });

  const onStatusChange = useCallback(
    (completion: TaskCompletion, status: TaskCompletionStatusEnum) => {
      // Each completion belongs to its own task instance (`completion.taskId`),
      // which can differ from the group's anchor task — never fall back to it,
      // or the wrong task's status gets updated.
      if (!completion.taskId) return undefined;
      return updateStatus({
        variables: { taskId: completion.taskId, attributes: { status } },
      });
    },
    [updateStatus],
  );

  const onCreateInterventionForSelected = useCallback(
    (completions: TaskCompletion[]) =>
      navigateToInterventionCreate(navigate, {
        // Task Reporting's `dashboardTaskShort` selects the location as flat
        // ids, the Timeline's `dashboardTimelineTasks` only as nested objects.
        assetId: task?.assetId || task?.asset?.id,
        siteAreaId: task?.siteAreaId || task?.siteArea?.id,
        siteId: task?.siteId || task?.site?.id,
        taskId: task?.id || taskId,
        taskCompletions: completions.map(getTaskCompletionSelectOption),
      }),
    [navigate, task, taskId],
  );

  return { onStatusChange, isStatusUpdating, onCreateInterventionForSelected };
}
